import React, { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibMoreHorizIcon } from '@chewy/kib-icons-react'
import { type Constant, Utils } from '@pbt/pbt-ui-components'

import type { DashboardLabTestEntry } from '~/api/graphql/generated/types'
import type { PopperAction } from '~/components/common/ActionsPopper'
import RhapActionsPopper from '~/components/common/RhapActionsPopper'
import DialogNames from '~/constants/DialogNames'
import LabVendorName from '~/constants/labVendorName'
import {
  LabTestState,
  LabTestStateIsDone,
  OrderType,
} from '~/constants/SOAPStates'
import i18n from '~/locales/i18n'
import { partialEditOrder } from '~/store/actions/orders'
import { useDashboardLabTestOrderDecline } from '~/store/hooks/orders'
import { getLabTestsStates, getLabVendors } from '~/store/reducers/constants'
import { getSoapId } from '~/store/reducers/soap'
import useDialog from '~/utils/useDialog'

import styles from './LabsHistoryWidget.module.scss'

interface LabActionsCellProps {
  clientId: string
  item: DashboardLabTestEntry
  patientId: string
}

export const createViewSoapAction = (onViewSoap: () => void): PopperAction => ({
  id: 'view-soap',
  label: i18n.t('Common:VIEW_SOAP'),
  onClick: onViewSoap,
})

export const createPrintLabelAction = (
  onPrintLabel: () => void,
): PopperAction => ({
  id: 'print-label',
  label: i18n.t('Common:PRINT_LABEL'),
  onClick: onPrintLabel,
})

export const createPrintOrderAction = (
  onPrintOrder: () => void,
): PopperAction => ({
  id: 'print-order',
  label: i18n.t('Common:PRINT_ORDER'),
  onClick: onPrintOrder,
})

const getActions = (
  item: DashboardLabTestEntry,
  labVendors: Constant[],
  labTestsStates: Constant[],
  {
    onPrintLabel,
    onPrintOrder,
    onPrintResults,
    onEmailResults,
    onViewSoap,
    onClientDeclined,
  }: {
    onClientDeclined: () => void
    onEmailResults: () => void
    onPrintLabel: () => void
    onPrintOrder?: () => void
    onPrintResults: () => void
    onViewSoap: () => void
  },
  currentSoapId?: string,
): PopperAction[] => {
  const isInHouse =
    item.vendor?.id ===
    Utils.findConstantIdByName(LabVendorName.INTERNAL, labVendors)
  const isCompleted = LabTestStateIsDone.includes(
    Utils.getConstantName(item.order?.status?.id, labTestsStates),
  )

  const soapId = item.soap?.id || item.order?.soap?.id
  const hasDifferentSoapId = Boolean(soapId && soapId !== currentSoapId)

  if (isInHouse) {
    if (isCompleted) {
      return [
        createPrintLabelAction(onPrintLabel),
        ...(hasDifferentSoapId ? [createViewSoapAction(onViewSoap)] : []),
      ]
    }
    return [
      createPrintLabelAction(onPrintLabel),
      {
        id: 'client-declined',
        label: i18n.t('Common:CLIENT_DECLINED'),
        onClick: onClientDeclined,
      },
      ...(hasDifferentSoapId ? [createViewSoapAction(onViewSoap)] : []),
    ]
  }

  // Reference lab
  if (isCompleted) {
    return [
      ...(onPrintOrder ? [createPrintOrderAction(onPrintOrder)] : []),
      {
        id: 'print-results',
        label: i18n.t('Common:PRINT_RESULTS'),
        onClick: onPrintResults,
      },
      {
        id: 'email-results',
        label: i18n.t('Common:EMAIL_RESULTS_ACTION'),
        onClick: onEmailResults,
      },
      ...(hasDifferentSoapId ? [createViewSoapAction(onViewSoap)] : []),
    ]
  }
  return [
    createPrintLabelAction(onPrintLabel),
    ...(onPrintOrder ? [createPrintOrderAction(onPrintOrder)] : []),
    ...(hasDifferentSoapId ? [createViewSoapAction(onViewSoap)] : []),
  ]
}

const LabActionsCell = ({ clientId, item, patientId }: LabActionsCellProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dispatch = useDispatch()

  const LabVendors = useSelector(getLabVendors)
  const LabTestStates = useSelector(getLabTestsStates)
  const currentSoapId = useSelector(getSoapId)

  const [openOrderResultsDialog] = useDialog(DialogNames.ORDER_RESULTS)
  const [openOrderResultsEmailDialog] = useDialog(
    DialogNames.EMAIL_LAB_ORDER_RESULT,
  )
  const [openLabelPrintDialog] = useDialog(DialogNames.PRINT_LAB_ORDER_LABEL)

  const declineLabTest = () =>
    item.order?.id &&
    dispatch(
      partialEditOrder({
        id: item.order?.id,
        type: OrderType.LAB_TEST,
        stateId: Utils.findConstantIdByName(
          LabTestState.DECLINED,
          LabTestStates,
        ),
      }),
    )

  const { declineDisabled } = useDashboardLabTestOrderDecline(item.order)

  const handlePrintLabel = () => {
    openLabelPrintDialog({
      labOrderId: item.order?.externalId,
      vendorId: item.vendor?.id,
      orderDate: item.orderDate,
      clientId,
      patientId,
    })
  }

  const handlePrintOrder = () => {
    openOrderResultsDialog({
      pdfUrl: item.order?.pdfUrl,
      vendorId: item.vendor?.id,
      orderId: item.order?.id,
      hasResults: false,
    })
  }

  const handlePrintResults = () => {
    openOrderResultsDialog({
      pdfUrl: item.order?.result?.pdfUrl,
      vendorId: item.vendor?.id,
      orderId: item.order?.id,
      hasResults: true,
    })
  }

  const handleEmailResults = () => {
    openOrderResultsEmailDialog({
      clientId,
      patientId,
      orderId: item.order?.id,
      vendorId: item.vendor?.id,
      soapId: item.soap?.id,
    })
  }

  const handleViewSoap = () => {
    const soapId = item.soap?.id || item.order?.soap?.id
    if (soapId) {
      window.open(`/soap/${soapId}`, '_blank')
    }
  }

  const handleClientDeclined = () => {
    if (!declineDisabled) {
      declineLabTest()
    }
  }

  const actions = getActions(
    item,
    LabVendors,
    LabTestStates,
    {
      onPrintLabel: handlePrintLabel,
      onPrintOrder: item.order?.pdfUrl ? handlePrintOrder : undefined,
      onPrintResults: handlePrintResults,
      onEmailResults: handleEmailResults,
      onViewSoap: handleViewSoap,
      onClientDeclined: handleClientDeclined,
    },
    currentSoapId,
  )

  return (
    <div className={styles.actionsContainer}>
      <KibButtonNew
        aria-label={i18n.t('Common:ACTION_OTHER')}
        className={styles.actionsButton}
        emphasis="tertiary"
        icon={<KibMoreHorizIcon />}
        ref={buttonRef}
        size="small"
        theme="utility"
      />
      <RhapActionsPopper
        actions={actions}
        placement="bottom"
        popperModifiers={[
          {
            name: 'offset',
            options: {
              offset: [50, 5],
            },
          },
        ]}
        trigger={buttonRef}
      />
    </div>
  )
}

export default LabActionsCell
